import { useState } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { FiX } from 'react-icons/fi';
import { toast } from 'react-toastify';
import PresentationModal from '../../organisms/PresentationModal';
import { getValidationData, updateValidationItem, getTagColor, setTagColor } from '../../../services/validationStore';

interface ValidationItem {
    id: number;
    operation_id: string;
    assignor: string;
    detail: string[];
    observation: string;
}

interface DetailTag {
    text: string;
    color: string;
}

const tagColors = [
    { value: 'blue', label: 'Azul', class: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' },
    { value: 'purple', label: 'Roxo', class: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300' },
    { value: 'red', label: 'Vermelho', class: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300' },
    { value: 'amber', label: 'Marrom', class: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300' },
    { value: 'gray', label: 'Cinza', class: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300' },
    { value: 'yellow', label: 'Amarelo', class: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300' },
    { value: 'green', label: 'Verde', class: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' },
];

const ValidationModalEdit = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { onDataChange } = useOutletContext<{ onDataChange: () => void }>();
    
    console.log('Modal renderizado - ID:', id);
    
    const item = getValidationData().find((d) => d.id === Number(id));
    
    console.log('Item encontrado:', item);
    
    const [details, setDetails] = useState<DetailTag[]>(
        (item?.detail || []).map(text => ({ text, color: getTagColor(text) }))
    );
    const [newTag, setNewTag] = useState('');
    const [editingTagIndex, setEditingTagIndex] = useState<number | null>(null);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [observation, setObservation] = useState(item?.observation || '');

    if (!item) {
        return <div className="p-4 text-red-600">Item não encontrado - ID: {id}</div>;
    }

    // Banco de tags existentes (todas as tags já usadas)
    const allExistingTags = Array.from(
        new Set(getValidationData().flatMap(item => item.detail))
    ).sort();

    const handleAddTag = () => {
        if (newTag.trim()) {
            const isDuplicate = details.some(d => d.text === newTag.trim());
            if (isDuplicate) {
                toast.error('Esta tag já foi adicionada');
                return;
            }
            const color = getTagColor(newTag.trim());
            setDetails([...details, { text: newTag.trim(), color }]);
            setNewTag('');
            setShowSuggestions(false);
        }
    };

    const handleSelectSuggestion = (suggestion: string) => {
        const isDuplicate = details.some(d => d.text === suggestion);
        if (isDuplicate) {
            toast.error('Esta tag já foi adicionada');
            return;
        }
        const color = getTagColor(suggestion);
        setDetails([...details, { text: suggestion, color }]);
        setNewTag('');
        setShowSuggestions(false);
    };

    const filteredSuggestions = allExistingTags.filter(
        tag => tag.toLowerCase().includes(newTag.toLowerCase()) && 
               !details.some(d => d.text === tag)
    );

    const handleRemoveTag = (index: number) => {
        setDetails(details.filter((_, i) => i !== index));
    };

    const handleChangeTagColor = (index: number, color: string) => {
        const updated = [...details];
        updated[index].color = color;
        setDetails(updated);
        setTagColor(updated[index].text, color); // Salva no store
        setEditingTagIndex(null);
    };

    const handleSave = () => {
        updateValidationItem(Number(id), {
            detail: details.map(d => d.text),
            observation: observation
        });
        toast.success('Validação atualizada com sucesso!');
        onDataChange();
        navigate(-1);
    };

    return (
        <PresentationModal
            id="validation-edit-modal"
            show={true}
            onClose={() => navigate(-1)}
            header={`Editar Validação - ${item.operation_id}`}
        >
            <div className="p-6 space-y-6">
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    {item.assignor}
                </div>

                {/* Detalhes (Tags) */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Detalhes
                    </label>
                    <div className="relative">
                        <div className="flex gap-2 mb-3">
                            <input
                                type="text"
                                value={newTag}
                                onChange={(e) => {
                                    setNewTag(e.target.value);
                                    setShowSuggestions(true);
                                }}
                                onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                                onFocus={() => setShowSuggestions(true)}
                                placeholder="Adicionar detalhe..."
                                className="flex-1 px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                            />
                            <button
                                onClick={handleAddTag}
                                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 dark:bg-gray-500 dark:hover:bg-gray-600"
                            >
                                Adicionar
                            </button>
                        </div>
                        
                        {/* Suggestions dropdown */}
                        {showSuggestions && newTag && filteredSuggestions.length > 0 && (
                            <div className="absolute z-20 w-full bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-md shadow-lg max-h-48 overflow-y-auto">
                                {filteredSuggestions.map((suggestion, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => handleSelectSuggestion(suggestion)}
                                        className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-slate-600 text-sm text-gray-900 dark:text-white"
                                    >
                                        {suggestion}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                        {details.map((tag, idx) => (
                            <div key={idx} className="relative">
                                <span
                                    onClick={() => setEditingTagIndex(idx)}
                                    className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm cursor-pointer hover:opacity-80 ${tagColors.find(c => c.value === tag.color)?.class || tagColors[0].class}`}
                                >
                                    {tag.text}
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRemoveTag(idx);
                                        }}
                                        className="hover:opacity-70"
                                    >
                                        <FiX size={14} />
                                    </button>
                                </span>
                                
                                {/* Color picker popup */}
                                {editingTagIndex === idx && (
                                    <div className="absolute z-10 mt-2 p-3 bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-gray-200 dark:border-slate-600">
                                        <div className="grid grid-cols-4 gap-2">
                                            {tagColors.map(color => (
                                                <button
                                                    key={color.value}
                                                    onClick={() => handleChangeTagColor(idx, color.value)}
                                                    className={`w-8 h-8 rounded-full ${color.class} hover:scale-110 transition-transform`}
                                                    title={color.label}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Observação */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Observação
                    </label>
                    <textarea
                        value={observation}
                        onChange={(e) => setObservation(e.target.value)}
                        rows={6}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                        placeholder="Digite a observação..."
                    />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                    <button
                        onClick={() => navigate(-1)}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 dark:bg-slate-700 dark:text-gray-300 dark:hover:bg-slate-600"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleSave}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
                    >
                        Salvar
                    </button>
                </div>
            </div>
        </PresentationModal>
    );
};

export default ValidationModalEdit;
